import { useQuery } from '@apollo/client';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as R from 'ramda';
import { Link } from 'react-router';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import {
  BookmarkParentType,
  BookmarkTargetType,
} from '~/apollo/generated/v3/graphql';
import { graphql } from '~/apollo/generated/v4';
import { LifecycleStatus } from '~/apollo/generated/v4/graphql';
import { NotFound } from '~/components/common/NotFound';
import { Panel } from '~/components/common/Panel';
import { PictureThumbnailV4 } from '~/components/common/picture-thumbnail-v4';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { useShowUnpublished } from '~/components/upload/supporting-object/use-show-unpublished';
import { useRouteParam } from '~/hooks/routing';
import { studyRoute } from '~/paths';
import { createRefetchQueries } from '~/utils/graphql';
import { soBookmarkPath } from '~/utils/modules/bookmark';
import { countUnpublishedItems } from '~/utils/modules/supporting-object';

const OUTCROP_PICTURES_PAGE = graphql(`
  query OutcropPicturesPage($id: ID!) {
    outcropGet(id: $id) {
      id
      pictures {
        ...pictureParts
        pictureUrl
        thumbnailUrl: pictureUrl(width: 500)
        file {
          ...fileParts
        }
      }
      studies {
        id
        name
        lifecycleStatus
        collectedBy
        collectedYear
        pictures {
          ...pictureParts
          pictureUrl
          thumbnailUrl: pictureUrl(width: 500)
          file {
            ...fileParts
          }
        }
      }
    }
  }
`);

export default function OutcropPicturesPage() {
  return (
    <ApolloProviderV4>
      <OutcropPicturesPageInner />
    </ApolloProviderV4>
  );
}

function OutcropPicturesPageInner() {
  const outcropId = useRouteParam('outcropId', parseInt);

  const { data, loading } = useQuery(OUTCROP_PICTURES_PAGE, {
    variables: { id: outcropId },
  });

  const refetchQueries = createRefetchQueries([
    { query: OUTCROP_PICTURES_PAGE, variables: { id: outcropId } },
  ]);

  const outcrop = data?.outcropGet;
  const allOutcropPictures = outcrop?.pictures ?? [];
  const studies = outcrop?.studies ?? [];
  type Study = (typeof studies)[number];

  const taggedStudyPictures = studies
    .flatMap(study => study.pictures)
    .filter(picture => picture.outcropTagId === outcropId);

  const unpublishedCount = countUnpublishedItems(
    allOutcropPictures.concat(taggedStudyPictures),
  );

  const { showUnpublished, PublishedToggle } =
    useShowUnpublished(unpublishedCount);

  const outcropPictures = allOutcropPictures
    .filter(
      p => showUnpublished || p.lifecycleStatus === LifecycleStatus.Published,
    )
    .sort(R.ascend(p => p.priority ?? Infinity));

  function studyTaggedPictures(study: Study) {
    return study.pictures
      .filter(picture => {
        if (picture.outcropTagId !== outcropId) return false;
        return (
          showUnpublished ||
          picture.lifecycleStatus === LifecycleStatus.Published
        );
      })
      .sort(R.ascend(p => p.priority ?? Infinity));
  }

  function authorLine(study: Study) {
    if (!study) return null;
    if (!study.collectedBy && !study.collectedYear) {
      return null;
    }

    return (
      <div className="space-x-2">
        {study.collectedBy && <span>{study.collectedBy}</span>}
        {study.collectedYear && (
          <span className="text-muted">{study.collectedYear}</span>
        )}
      </div>
    );
  }

  if (loading) return <SpinnerPlaceholder />;
  if (!outcrop) return <NotFound />;

  return (
    <div className="space-y-4">
      <PublishedToggle />

      <div className="grid md:grid-cols-2 gap-4">
        {outcropPictures.map(picture => (
          <div key={picture.id}>
            <PictureThumbnailV4
              picture={picture}
              imgSrc={picture.pictureUrl}
              thumbnailImgSrc={picture.thumbnailUrl}
              bookmarkable={{
                parentType: BookmarkParentType.Outcrop,
                parentId: outcropId,
                targetType: BookmarkTargetType.Picture,
                targetId: parseInt(picture.id),
                path: soBookmarkPath(
                  'outcrop',
                  outcropId,
                  BookmarkParentType.Outcrop,
                  picture.outcropTagId,
                ),
              }}
              refetchQueries={refetchQueries}
            />
          </div>
        ))}
      </div>

      {studies.map(study => {
        const taggedPics = studyTaggedPictures(study);
        if (!taggedPics.length) return null;

        return (
          <Panel key={study.id}>
            <Panel.Heading className="flex justify-between items-center">
              <Panel.Title>
                <strong>{study.name}</strong>
                {authorLine(study)}
              </Panel.Title>

              <Link
                to={studyRoute(parseInt(study.id))}
                target="_blank"
                className="btn btn-ghost btn-xs"
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} /> View Study
              </Link>
            </Panel.Heading>

            <Panel.Body>
              <div className="grid md:grid-cols-2 gap-4">
                {taggedPics.map(picture => (
                  <div key={picture.id}>
                    <PictureThumbnailV4
                      picture={picture}
                      imgSrc={picture.pictureUrl}
                      thumbnailImgSrc={picture.thumbnailUrl}
                      bookmarkable={{
                        parentType: BookmarkParentType.Study,
                        parentId: parseInt(study.id),
                        targetType: BookmarkTargetType.Picture,
                        targetId: parseInt(picture.id),
                        path: soBookmarkPath(
                          'study',
                          parseInt(study.id),
                          BookmarkParentType.Study,
                          picture.outcropTagId,
                        ),
                      }}
                      refetchQueries={refetchQueries}
                    />
                  </div>
                ))}
              </div>
            </Panel.Body>
          </Panel>
        );
      })}
    </div>
  );
}
